import React, { useEffect, useState } from 'react';
import { Row, Col, Flex } from 'antd';
import { PageSection, PageTitle, PageContent } from '../../styles/global/Pages.styled';
import { Text } from '../Global/Atomics/Text';
import { useTranslation } from 'react-i18next';
import { useGlobalFilterContext } from '../../contexts/GlobalFilterProvider';
import useFetch from '../../server/useFetch';
import { NewWidget } from '../Global/Atomics/NewWidget';
import { isEmpty } from '../../utils/hooks/isEmpty';
import i18next from 'i18next';
import { ComplexTable } from "@synergy/table";
import { containerMiddlewareAdEPorto } from '../../server/constants.api';
import { EventsMapComponent } from '../Global/Molecules/Maps/EventsMapComponent';
import { SelectAtomic } from '../Global/Atomics/SelectAtomic';
import { generateYearList } from '../../utils/hooks/generateYeas';

// Define a functional component named Events
export const Events = () => {
  // Retrieve global filter content and translation function from context and i18n
  const { filterContent, isToApplyFilters, setIsToApplyFilters, entityToFetch } = useGlobalFilterContext();
  const { t } = useTranslation();
  const [mapShowInfo, setMapShowInfo] = useState(false);
  const [yearToFetch, setYearToFetch] = useState(new Date().getFullYear());

  const { data: eventsByPriority, isFetching: eventsByPriorityIsFetching, refetch: refetchEventsByPriority, } = useFetch(
    "events/priority",
    "eventsByPriority",
    {
      tenant: filterContent?.tenant,
      classes: filterContent?.classes?.join(), //Para enviar mais que uma enviar com ; (ex. Office;Lab)
      startDate: `${yearToFetch}-01-01T00:00:00`,
      endDate: `${yearToFetch}-12-31T23:59:59`,
    },
    false,
    null,
    containerMiddlewareAdEPorto
  );

  const { data: eventsDensity, isFetching: eventsDensityIsFetching, refetch: refetchEventsDensity } = useFetch(
    "events/density",
    "eventsDensity",
    {
      id: filterContent?.id,
      classes: filterContent?.classes?.join(), //Para enviar mais que uma enviar com ; (ex. Office;Lab),
      startDate: `${yearToFetch}-01-01T00:00:00`,
      endDate: `${yearToFetch}-12-31T23:59:59`,
    },
    false,
    null,
    containerMiddlewareAdEPorto
  );

  const { data: eventsList, isFetching: eventsListIsFetching, refetch: refetchEventsList } = useFetch(
    "events",
    "eventsList",
    {
      tenant: filterContent?.tenant,
      classes: filterContent?.classes?.join(), //Para enviar mais que uma enviar com ; (ex. Office;Lab),
      startDate: `${yearToFetch}-01-01T00:00:00`,
      endDate: `${yearToFetch}-12-31T23:59:59`,
    },
    false,
    null,
    containerMiddlewareAdEPorto
  );

  useEffect(() => {
    if (isToApplyFilters) {
      setIsToApplyFilters(false);
      refetchEventsByPriority();
      refetchEventsDensity();
      refetchEventsList();
    }
  }, [isToApplyFilters]);

  useEffect(() => {
    refetchEventsByPriority();
    refetchEventsDensity();
    refetchEventsList();
  }, [yearToFetch]);

  // Render the Events component
  return (
    // Render the content of the page
    <PageContent>
      {/* Render the first section with a specific background color */}
      <PageSection $backgroundColor='neutral-1' >
        {/* Use Flex component to align elements and create space between them */}
        <Flex align="center" justify="space-between">
          {/* Render the title of the section */}
          <PageTitle>
            <div className="title">
              {/* Render a text component with specific styles and translated text */}
              <Text $fontColor='coolgray-12' $fontSize='text_xl' $weight='regular' $marginRight='s03'>{t('events')}</Text>
            </div>
          </PageTitle>
          <div style={{ width: '100px' }}>
            <SelectAtomic
              optionsData={generateYearList(2022)}
              handleOptionsChange={(value) => setYearToFetch(value)}
              defaultValue={yearToFetch}
              fullWidth={true}
            />
          </div>
        </Flex>
      </PageSection>

      {/* Render another section with a different background color */}
      <PageSection $backgroundColor='neutral-1' className='first-section'>
        <Row gutter={[32, 32]}>
          <Col span={8}>
            <NewWidget data={{
              background: 'neutral-1',
              title: t('Events Severity'),
              icon: 'Alert',
              background: 'white',
              description: null,
              tooltip: null,
              charts: [
                {
                  chartType: 'bigNumberChart',
                  height: '60px',
                  width: '100%',
                  data: { number: !isEmpty(eventsByPriority) && eventsByPriority?.data ? Object.values(eventsByPriority?.data)?.reduce((acc, curr) => acc + curr, 0) : 0, isToGetConfigurations: false },
                },
                {
                  chartType: 'horizontalStackedBarChart',
                  height: '27px',
                  width: '100%',
                  chartId: 'horizontalStackedBarChartOne',
                  data: {
                    data: [{
                      name: "Events",
                      values: [
                        {
                          name: t("critical"),
                          value: eventsByPriority?.data['CRITICAL'] ?? 0,
                        },
                        {
                          name: t("high"),
                          value: eventsByPriority?.data['HIGH'] ?? 0,
                        },
                        {
                          name: t("medium"),
                          value: eventsByPriority?.data['MEDIUM'] ?? 0,
                        },
                        {
                          name: t("low"),
                          value: eventsByPriority?.data['LOW'] ?? 0,
                        },
                        {
                          name: t("Info"),
                          value: eventsByPriority?.data['INFO'] ?? 0,
                        },
                      ]
                    }],
                    showLegend: false,
                    colors: ['#E82020', '#EE722E', '#FADA33', '#5BDA77', '#62B5F4'],
                    id: 'horizontalStackedBarChartOne',
                    title: null,
                    withBarValues: false,
                    legends: false,
                    withLineValues: true,
                    xAxisLabel: null,
                    yAxisLabel: '',
                    hideAxis: true,
                    margins: {
                      left: 10
                    },
                    barSize: 32,//6/12/16/24/32/40/56,
                    isToGetConfigurations: false
                  }
                }
              ]
            }} />
          </Col>
          <Col span={16}>
            <Row gutter={[32, 32]}>
              <Col span={7}>
                <NewWidget
                  data={{
                    title: t('critical'),
                    background: "white", // Define o fundo do widget como branco
                    iconWarper: true,
                    iconColor: 'critical-base',
                    charts: [
                      {
                        chartType: "bigNumberChart", // Tipo do gráfico
                        chartId: "eventCritial", // ID do gráfico
                        height: "87px", // Altura do gráfico
                        isFetching: eventsByPriorityIsFetching,
                        data: { number: eventsByPriority?.data['CRITICAL'] ?? 0 }, // Dados para o gráfico
                      },
                    ],
                    height: "100%", // Altura do gráfico
                  }}
                />
              </Col>
              <Col span={7}>
                <NewWidget
                  data={{
                    title: t('high'),
                    background: "white", // Define o fundo do widget como branco
                    iconWarper: true,
                    iconColor: 'high-base',
                    charts: [
                      {
                        chartType: "bigNumberChart", // Tipo do gráfico
                        chartId: "eventHigh", // ID do gráfico
                        height: "87px", // Altura do gráfico
                        isFetching: eventsByPriorityIsFetching,
                        data: { number: eventsByPriority?.data['HIGH'] ?? 0 }, // Dados para o gráfico
                      },
                    ],
                    height: "100%", // Altura do gráfico
                  }}
                />
              </Col>
              <Col span={10}>
                <NewWidget
                  data={{
                    title: t('others'),
                    background: "white", // Define o fundo do widget como branco
                    charts: [
                      {
                        chartType: "dataChart", // Tipo do gráfico
                        chartId: "eventHigh", // ID do gráfico
                        height: "87px", // Altura do gráfico
                        isFetching: eventsByPriorityIsFetching,
                        data: {
                          data: [
                            {
                              number: eventsByPriority?.data?.['MEDIUM'] ?? "0",
                              unit: t("medium"),
                              label: "No data for homologous period comparison",
                              fluctuation: null,
                              circleColor: 'medium-base'
                            },
                            {
                              number: eventsByPriority?.data?.['LOW'] ?? "0",
                              unit: t("low"),
                              label: "No data for homologous period comparison",
                              fluctuation: null,
                              circleColor: 'low-base'
                            },
                            {
                              number: eventsByPriority?.data?.['INFO'] ?? "0",
                              unit: "Info",
                              label: "No data for homologous period comparison",
                              fluctuation: null,
                              circleColor: 'info-base'
                            }], // Dados para o gráfico
                          withMargin: true,
                          alignment: 'flex-start',
                          justify: 'center'
                        }
                      },
                    ],
                    height: "100%", // Altura do gráfico
                  }}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </PageSection>

      {/* Render another section with a white background */}
      <PageSection $backgroundColor='white'>
        <Row gutter={[32, 32]}>
          <Col span={24}>
            <Row gutter={[32, 32]}>
              <Col span={24}>
                <NewWidget
                  data={{
                    title: t('eventsMap'),
                    background: null,
                    border: 'neutral-3',
                    height: '603px',
                    height: "100%", // Altura do gráfico
                    switch: [
                      {
                        title: t('Show info'),
                        onClick: () => setMapShowInfo(!mapShowInfo),
                      }
                    ]
                  }}
                >
                  <EventsMapComponent tenantId={filterContent?.id} classes={filterContent?.classes} mapShowInfo={mapShowInfo} yearToFetch={yearToFetch}/>
                </NewWidget>
              </Col>
              <Col span={24}>
                <NewWidget
                  data={{
                    title: 'eventDensity',
                    icon: 'Clipboard',
                    background: null,
                    border: 'neutral-3',
                    tooltip: t("The density map takes into account the longevity of the event"),
                    charts: [
                      {
                        chartType: "heatmapChart", // Tipo do gráfico
                        chartId: "heatmapChartOne", // ID do gráfico
                        height: '447px', // Altura do gráfico
                        isFetching: eventsDensityIsFetching,
                        data: eventsDensity?.data ? getDensity(eventsDensity?.data, t) : [], // Dados para o gráfico
                      },
                    ],
                    height: "100%", // Altura do gráfico
                  }}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </PageSection>

      {/* Render another section with a white background */}
      <PageSection>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <h3>{t("Event List")}</h3>
          </Col>
          <Col span={24}>
            <ComplexTable
              // tableId="eventsTable"
              language={i18next.resolvedLanguage}
              fetchData={eventsList?.data.map((item) => {
                const openDate = item.openDate?.split('T');
                return { ...item, class: t(item.class), openDate: openDate?.[0] + ' ' + openDate?.[1] };
              }) ?? []}
              tableConfiguration={eventsTableConfigurations(t)}
              pagination={{ pageSize: 5, showSizeChanger: true, pageSizeOptions: ['5', '10', '20', '30', '40'] }}
              // defaultCurrent={2}
              // hidePagination={false}
              hideFilterBar={true}
              isLoading={eventsListIsFetching}
            />
          </Col>
        </Row>
      </PageSection>
    </PageContent>
  );
}

const getDensity = (data, t) => {
  const months = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
  const result = months.map(month => {
    return {
      name: t(month),
      data: []
    }
  });

  Object.entries(data).forEach(([key, value]) => {
    const keyDay = key.split('-')[2];
    const keyMonth = parseInt(key.split('-')[1], 10) - 1;
    const valueCount = isEmpty(value) ? 0 : Object.values(value).reduce((acc, curr) => acc + curr);

    result.filter(month => month.name === t(months[keyMonth]))?.[0]?.data.push({
      x: keyDay,
      y: valueCount,
    });
  });
  return {
    series: result, unit: t("events"), "yAxisLabel": t("months"),
    "xAxisLabel": t("days"),
  };
};

const eventsTableConfigurations = (t) => {
  return {
    collumnsConfiguration: [
      {
        "title": t("Severity"),
        "dataIndex": "severity",
        "key": "severity",
        "filterMultipleSelect": true
      },
      {
        "title": t("Open Date"),
        "dataIndex": "openDate",
        "key": "openDate"
      },
      {
        "title": t("Class"),
        "dataIndex": "class",
        "key": "class",
        "filterMultipleSelect": true
      },
      {
        "title": t("Name"),
        "dataIndex": "parentName",
        "key": "name",
        "width": '20%'
      },
      {
        "title": t("Description"),
        "dataIndex": "description",
        "key": "description",
        "width": '50%'
      },
      {
        "title": t("Category"),
        "dataIndex": "category",
        "key": "category"
      }
    ],
    hideSelectAll: true
  };
};